// externals
import { isEmpty } from 'lodash';

// libraries
import type { CalculationDetails } from '@makemydeal/dr-dash-bff-types';
import { ProductCalculationEngineService } from '@makemydeal/dr-dash-bff-types';
import type { CalculationDetailsState, StateTree } from '@makemydeal/dr-dash-types';

// selectors
import * as offerReduxSelectors from './offerRedux';
import { CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import * as paymentSelectors from './payment';
import * as offerInfoSelectors from './offerInfoSelectors';
import { PENDING_DEALER_REVIEW_PAYMENT_STATUS } from '@makemydeal/dr-shared-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

export const getCalculationDetailsState = (state: StateTree): CalculationDetailsState => {
    return state.calculationDetails || ({} as any);
};

export const getCalculationDetails = (state: StateTree): CalculationDetails | undefined => {
    const calculationDetails = getCalculationDetailsState(state);

    if (isEmpty(calculationDetails)) {
        return undefined;
    }

    return calculationDetails;
};

/**
 * The status a pending deal is saved with once it has been reviewed, or `undefined` to leave the payload's
 * own calculation state alone. Not gated on `isPaymentsCalculated`, unlike the Failed -> Current promotion
 * in the caller: that is always false for a pending deal, because initialisation was suppressed, so
 * reusing the gate would make this unreachable.
 */
const resolvePendingPaymentStatus = (
    state: StateTree,
    currentPaymentStatus: string | undefined
): { paymentStatus: 'Current' | 'Failed' } | undefined => {
    if (currentPaymentStatus !== PENDING_DEALER_REVIEW_PAYMENT_STATUS) {
        return undefined;
    }

    const resolution = offerInfoSelectors.getPendingReviewResolution(state);
    const isPriced = offerInfoSelectors.getHasSuccessfulPaymentSinceInit(state);

    if (!resolution && !isPriced) {
        return undefined;
    }

    return { paymentStatus: resolution === 'resolvedWithFailure' && !isPriced ? 'Failed' : 'Current' };
};

export const createCalculationDetailsForOfferSave = (state: StateTree): CalculationDetails | undefined => {
    const rootCalcDetails = getCalculationDetails(state);
    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    const cashApplyMethod =
        rootCalcDetails?.payment?.calculationSettings?.cashApplyMethod || offerReduxSelectors.getCashOption(state);
    const currentCalculationEndpoint = offerReduxSelectors.getCalculationEndpointByOfferType(state, offerType);

    const dealerDiscounts = offerReduxSelectors.getOfferDealerDiscounts(state);
    const customRateGroup = offerReduxSelectors.getCustomRateGroup(state);
    const equityAllocationOrder = offerReduxSelectors.getEquityAllocationMethod(state);

    const shouldIncludeCashApplyMethod = offerType === LEASE && cashApplyMethod != null;

    const currentPaymentStatus = rootCalcDetails?.payment?.calculationState?.paymentStatus;
    const isPaymentsCalculated = paymentSelectors.getPaymentsCalculated(state);
    const isToggleEnabled = featureToggleSelectors.isGracefulRecalcFailureEnabled(state);

    const residualType = featureToggleSelectors.isLeaseResidualTreatmentEnabled(state)
        ? offerReduxSelectors.getResidualType(state) ?? offerReduxSelectors.getResidualInfoOverride(state)?.baseResidualType
        : undefined;

    const resolvedPendingCalculationState = resolvePendingPaymentStatus(state, currentPaymentStatus);

    const calculationState =
        resolvedPendingCalculationState ??
        (isToggleEnabled && currentPaymentStatus === 'Failed' && isPaymentsCalculated
            ? { paymentStatus: 'Current' as const }
            : rootCalcDetails?.payment?.calculationState);

    if (rootCalcDetails) {
        const payment = rootCalcDetails.payment;
        const calculationEngine = rootCalcDetails.payment?.calculationEngine;
        const calculationSettings = rootCalcDetails.payment?.calculationSettings;

        const updatedCalcDetails = {
            ...rootCalcDetails,
            ...(customRateGroup && { customRateGroup }),
            ...(equityAllocationOrder && { equityAllocationOrder }),
            payment: {
                ...payment,
                calculationEngine: {
                    ...calculationEngine,
                    endpoint: currentCalculationEndpoint
                },
                calculationSettings: {
                    ...calculationSettings,
                    ...(shouldIncludeCashApplyMethod && { cashApplyMethod })
                },
                ...(residualType && {
                    calculationOverrides: {
                        ...payment?.calculationOverrides,
                        residualType
                    }
                }),
                ...{ itemizedDiscounts: dealerDiscounts },
                ...(calculationState && { calculationState })
            }
        };

        if (cashApplyMethod && (offerType === FINANCE || offerType === CASH)) {
            delete updatedCalcDetails.payment.calculationSettings.cashApplyMethod;
        }
        return updatedCalcDetails;
    }

    return {
        ...(customRateGroup && { customRateGroup }),
        ...(equityAllocationOrder && { equityAllocationOrder }),
        payment: {
            calculationEngine: {
                endpoint: currentCalculationEndpoint
            },
            calculationSettings: {
                ...(shouldIncludeCashApplyMethod && { cashApplyMethod })
            },
            ...(residualType && {
                calculationOverrides: {
                    residualType
                }
            }),
            ...{ itemizedDiscounts: dealerDiscounts }
        }
    };
};

export const getPartnerCode = (state: StateTree): string | undefined => {
    return getCalculationDetailsState(state).payment?.calculationSourceIds?.partnerCode;
};

export const getPartnerDealerId = (state: StateTree): string | number | undefined => {
    return getCalculationDetailsState(state).payment?.calculationSourceIds?.partnerDealerId;
};

export const getProductsCalculationEngineService = (state: StateTree): ProductCalculationEngineService | undefined => {
    return getCalculationDetailsState(state).products?.calculationEngine?.service;
};

export const isFieCalculationEngineService = (state: StateTree): boolean => {
    const productsService = getProductsCalculationEngineService(state);

    return productsService === ProductCalculationEngineService.COX_FIE || productsService === undefined;
};

export const isCapReductionTaxCapitalized = (state: StateTree): boolean => {
    return getCalculationDetailsState(state).payment?.isCapReductionTaxCapitalized ?? false;
};

export const getIsFirstPaymentCapitalized = (state: StateTree): boolean | undefined => {
    return getCalculationDetailsState(state).payment?.isFirstPaymentCapitalized;
};
